import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {SalesRepDialogComponent} from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import {Entity} from '../entity/entity';
import {Opportunity} from '../opportunity/opportunity';
import {PersonLogin} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {OpportunityService} from '../services/opportunity-service';
import {PersonLoginService} from '../services/person-login-service';
import {PersonService} from '../services/person-service';
import {SelectionService} from '../services/selection-service';
import {DateUtils} from '../utils/date-utils';
import {StringUtils} from '../utils/string-utils';
import {AssociatedQuotesDialogComponent} from './associated-quotes-dialog.component';
import {AwardProbability} from './award-probability';
import {MatDialog} from '@angular/material/dialog';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { NgClass, DecimalPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'opportunity-detail',
    templateUrl: './opportunity-detail.component.html',
    styleUrls: ['./opportunity-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatMenuTrigger, MatMenu, MatMenuItem, MatProgressSpinner, NgClass, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, DecimalPipe, CurrencyPipe, DatePipe]
})
export class OpportunityDetailComponent implements OnInit, OnChanges {
  readonly opportunityId = input.required<string>({ alias: 'opportunity-id' });
  readonly opportunityRevision = input.required<string>({ alias: 'opportunity-revision' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly selectionService = inject(SelectionService);
  private readonly personService = inject(PersonService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);


  private personLogin!: PersonLogin;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  public opportunity!: Opportunity;
  readonly associatedQuote = signal<Entity>(undefined!);
  readonly associatedQuotes = signal<Entity[]>(undefined!);
  readonly statuses = signal<SelectionItem[]>([]);
  readonly phases = signal<SelectionItem[]>([]);
  readonly competitors = signal<SelectionItem[]>([]);
  readonly wonLossReasons = signal<SelectionItem[]>([]);
  private readonly awardProbabilities = signal<AwardProbability[]>([]);
  readonly marketSegmentDescription = signal<string>(undefined!);
  readonly projectTypeDescription = signal<string>(undefined!);
  readonly salesChannelDescription = signal<string>(undefined!);
  readonly leadTimeDescription = signal<string>(undefined!);
  readonly specTypeDescription = signal<string>(undefined!);

  constructor
  () {
    this.associatedQuote.set(new Entity());
  }

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin) {
      this.router.navigate(['main']);
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.getOpportunity(this.opportunityId(), this.opportunityRevision());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.getOpportunity(this.opportunityId(), this.opportunityRevision());
    }
  }

  private getOpportunity(id: string, revision: string): void {
    this.opportunityService.getOpportunity(id, +revision).subscribe({
      next: result => {
        this.opportunity = result;
        this.opportunityService.getAssociatedQuotes(id, +revision).subscribe(quotes => this.associatedQuotes.set(quotes));
        this.opportunityService.getAllAwardProbabilities().subscribe(awards => this.awardProbabilities.set(awards));
        if (result && result.associatedQuoteId && result.associatedQuoteId.length > 0) {
          let quoteId = this.opportunity.associatedQuoteId.substring(0, 8);
          let quoteRevision = this.opportunity.associatedQuoteId.substring(9);
          this.opportunityService.getAssociatedQuote(quoteId, +quoteRevision).subscribe(quote => {
            this.associatedQuote.set(quote);
            this.loadMarketSegmentDescription();
            this.loadProjectTypeDescription();
            this.loadSalesChannelDescription();
            this.loadLeadTimeDescription();
            this.loadSpecTypeDescription();
          });
        }
        this.loadSelectionLists();
        this.isBusy.set(false);
      }, error: error => this.handleError(error)
    });
  }

  public saveOpportunity(): void {
    if (!this.personLogin || this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['main']);
    } else {
      this.isBusy.set(true);
      this.opportunityService.save(this.opportunity).subscribe({
        next: result => {
          this.opportunity = result;
          this.isBusy.set(false);
        }, error: error => this.handleError(error)
      });
    }
  }

  private loadSelectionLists(): void {
    this.selectionService.getOpportunityStatusCodes().subscribe(status => this.statuses.set(status));
    this.selectionService.getOpportunityPhaseCodes().subscribe(phase => this.phases.set(phase));
    this.selectionService.getFpaCompetitors().subscribe(competitor => this.competitors.set(competitor));
    this.selectionService.getAllIecWinLossReasons().subscribe(reasons => this.wonLossReasons.set(reasons));
  }

  private loadMarketSegmentDescription(): void {
    this.selectionService.getSalesAreas().subscribe(results => {
      const filtered = results.find(salesArea => salesArea.code === this.associatedQuote().marketSegmentCode);
      if (filtered) {
        this.marketSegmentDescription.set(filtered.description);
      }
    });
  }

  private loadProjectTypeDescription(): void {
    this.selectionService.getRegions().subscribe(results => {
      const filtered = results.find(projectType => projectType.code === this.associatedQuote().projectTypeCode);
      if (filtered) {
        this.projectTypeDescription.set(filtered.description);
      }
    });
  }

  private loadSalesChannelDescription(): void {
    this.selectionService.getSalesChannels().subscribe(results => {
      const filtered = results.find(channel => channel.code === this.associatedQuote().salesChannelCode);
      if (filtered) {
        this.salesChannelDescription.set(filtered.description);
      }
    });
  }

  private loadLeadTimeDescription(): void {
    this.selectionService.getPriorities().subscribe(results => {
      const filtered = results.find(leadTime => leadTime.code === this.associatedQuote().leadTimeCode);
      if (filtered) {
        this.leadTimeDescription.set(filtered.description);
      }
    });
  }

  private loadSpecTypeDescription(): void {
    this.specTypeDescription.set('');
    this.selectionService.getNewExtendCodes().subscribe({
      next: (selections: SelectionItem[]) => {
        const filtered: SelectionItem[] = selections.filter((specType: SelectionItem) => specType.code === this.associatedQuote().specTypeCode);
        if ((filtered?.length || 0) > 0) {
          this.specTypeDescription.set(filtered[0].description);
        }
      }
    });
  }

  public refreshAwardProbabilityPercent(currentPhaseCode: string): void {
    this.opportunity.awardProbabilityPercent = this.awardProbabilities().find(
      awardProbability => awardProbability.statusCode === currentPhaseCode)!.awardProbabilityPercent;
  }

  public showOutsideSalesRep(): void {
    if (this.associatedQuote().outsideSalesRepId && this.associatedQuote().outsideSalesRepId.length > 0) {
      this.personService.getContactPerson(this.associatedQuote().outsideSalesRepId).subscribe(result => {
        let outsideRep = result;
        if (outsideRep) {
          let riserDialog = this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '480px',
            height: '170px',
            data: {rep: outsideRep},
          });
        }
      });
    }
  }

  public showSalesRep(): void {
    if (this.associatedQuote().salesRepId && this.associatedQuote().salesRepId.length > 0) {
      this.personService.getContactPerson(this.associatedQuote().salesRepId).subscribe(result => {
        let salesRep = result;
        if (salesRep) {
          let riserDialog = this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '480px',
            height: '170px',
            data: {rep: salesRep},
          });
        }
      });
    }
  }

  public viewQuotes(): void {
    if (this.associatedQuotes() && this.associatedQuotes().length > 0) {
      let riserDialog = this.dialog.open(AssociatedQuotesDialogComponent, {
        disableClose: true,
        width: '1360px',
        height: '440px',
        data: {quotes: this.associatedQuotes(), opportunity: this.opportunity},
      });
    }
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setExpectedBookDate(newValue: Date) {
    this.opportunity.expectedBookDate = DateUtils.formatOutput(newValue);
  }

  public setFirstReleaseExpectedDate(newValue: Date) {
    this.opportunity.firstReleaseExpectedDate = DateUtils.formatOutput(newValue);
  }

  public setExpectedFirstShipmentDate(newValue: Date) {
    this.opportunity.expectedFirstShipmentDate = DateUtils.formatOutput(newValue);
  }

  public setExpectedFinalShipmentDate(newValue: Date) {
    this.opportunity.expectedFinalShipmentDate = DateUtils.formatOutput(newValue);
  }

  public setWonLossDate(newValue: Date) {
    this.opportunity.wonLossDate = DateUtils.formatOutput(newValue);
  }

  public backToOpportunityMaster(): void {
    this.router.navigate(['opportunities']);
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  dateStartValue(input: Date): Date {
    if (!input) {
      return new Date();
    } else {
      const newDate = new Date(input);
      const yearValue = newDate.getFullYear();
      if (isNaN(yearValue) || yearValue < 2000) {
        return new Date();
      } else {
        return new Date(input);
      }
    }
  }

}
